import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Switch,
  Text
} from '@chakra-ui/react'

import { FormField } from '../../../../components/FormField'
import { Icon } from '../../../../components/Icon'
import ImageCropInput from '../../../../components/ImageCropInput/ImageCropInput'
import { RichTextField } from '../../../../components/RichTextField'
import { FormSelect, SearchableSelect } from '../../../../components/Select'
import { Tooltip } from '../../../../components/Tooltip'
import VideoFileInput from '../../../../components/VideoFileInput/VideoFileInput'
import { EventFormat, EventType, GeneralOverrideGroup, ITestimonialSlot } from '../../../../services/Event/types'
import { TARGET_AUDIENCE_HINT, TOPICS_HINT } from '../contentHints'
import { HELP_FORM_BLOCKED_HINT, HELP_FORM_RULE_TEXT } from '../helpForm'
import { LocationFields, LocationValue } from './LocationFields'

export type GeneralValue = LocationValue & {
  format: EventFormat | ''
  types: number[]
  thumb: File | null
  thumbPreview: string | null
  thumb_wide: File | null
  thumbWidePreview: string | null
  // Event video: a picked MP4 (video), the stored URL for preview (videoPreview),
  // and an explicit removal flag — mirrors the thumb File/preview pattern.
  video: File | null
  videoPreview: string | null
  remove_video: boolean
  copy: string
  target_audience: string
  topics: string
  min_installment_value: number | null
  use_term: boolean
  term: string | null
  use_testimonial: boolean
  testimonial_title: string | null
  testimonials: ITestimonialSlot[]
  active_campaign_lead_lists: string[]
  active_campaign_buyer_lists: string[]
  use_help_form: boolean
}

export const needsLocation = (format: EventFormat | '') => format === 'IN_PERSON' || format === 'HYBRID'

interface IProps {
  value: GeneralValue
  onChange: (patch: Partial<GeneralValue>) => void
  /** Which general groups to render. */
  groups: GeneralOverrideGroup[]
  eventTypes: EventType[]
  /** Available ActiveCampaign lists for the AC selects. */
  activeCampaignLists: { id: string; name: string }[]
  /** Effective format used to decide whether the location block is shown. */
  formatForLocation: EventFormat | ''
  disabled?: boolean
  /**
   * Whether the tickets of whoever this editor is editing reach the minimum price of the
   * "Tire suas dúvidas" form (see ../helpForm). False trava o switch — a API recusa a
   * combinação, aqui só evitamos o 422.
   */
  helpFormAllowed?: boolean
  /** Por que o switch está travado, no contexto de quem está editando (série x edição). */
  helpFormBlockedHint?: string
  errors?: Record<string, string[]>
  errorPrefix?: string
}

/**
 * Renders the "general" event fields. Used both for the series defaults (all
 * groups) and for an edition's override section (only the overridden groups).
 */
export function GeneralFieldsEditor({
  value,
  onChange,
  groups,
  eventTypes,
  activeCampaignLists,
  formatForLocation,
  disabled,
  helpFormAllowed = true,
  helpFormBlockedHint,
  errors = {},
  errorPrefix = ''
}: IProps) {
  const has = (group: GeneralOverrideGroup) => groups.includes(group)
  const err = (field: string) => errors[`${errorPrefix}${field}`]?.[0]

  return (
    <Flex direction="column" gap={4}>
      {has('format') && (
        <FormField label="Formato" error={err('format')} isRequired>
          <FormSelect
            isDisabled={disabled}
            value={value.format}
            onChange={(value) => onChange({ format: value as EventFormat })}
            options={[
              { value: 'IN_PERSON', label: 'Presencial' },
              { value: 'ONLINE', label: 'Online' },
              { value: 'HYBRID', label: 'Híbrido' }
            ]}
            placeholder="Selecione"
          />
        </FormField>
      )}

      {has('types') && (
        <FormField label="Tipo do Evento" error={err('types')} isRequired>
          <SearchableSelect<number>
            isMulti
            options={eventTypes.map((type) => ({ value: type.id, label: type.name }))}
            value={value.types}
            onChange={(types) => onChange({ types })}
            placeholder="Selecione os tipos"
            entity="tipo"
            isDisabled={disabled}
          />
        </FormField>
      )}

      {has('thumb') && (
        <ImageCropInput
          label="Thumb Padrão"
          value={value.thumb ?? value.thumbPreview ?? null}
          onChange={(file) => onChange({ thumb: file, thumbPreview: null })}
          aspectRatio={375 / 221}
          suggestedSize="375 x 221 px"
          previewAspectRatio="375 / 221"
          maxSizeMB={5}
          isDisabled={disabled}
          error={errors[`${errorPrefix}thumb`]}
        />
      )}

      {has('thumb_wide') && (
        <ImageCropInput
          label="Thumb Wide"
          value={value.thumb_wide ?? value.thumbWidePreview ?? null}
          onChange={(file) => onChange({ thumb_wide: file, thumbWidePreview: null })}
          aspectRatio={787 / 222}
          suggestedSize="787 x 222 px"
          previewAspectRatio="787 / 222"
          maxSizeMB={5}
          isDisabled={disabled}
          error={errors[`${errorPrefix}thumb_wide`]}
        />
      )}

      {has('video_url') && (
        <VideoFileInput
          label="Vídeo para página do evento (opcional)"
          file={value.video}
          existingUrl={value.remove_video ? null : value.videoPreview}
          onChange={(file) => onChange({ video: file, remove_video: false })}
          onRemoveExisting={() => onChange({ video: null, remove_video: true })}
          isDisabled={disabled}
          maxSizeMB={100}
          helperText="Envie um arquivo MP4 (até 100MB). Quando enviado, o vídeo substitui a Thumb Wide na página do evento."
          error={errors[`${errorPrefix}video`]}
        />
      )}

      {has('copy') && (
        <RichTextField
          label="Copy do Evento"
          value={value.copy}
          onChange={(copy) => onChange({ copy })}
          placeholder="Escreva a copy do evento aqui..."
          minH="200px"
        />
      )}

      {has('target_audience') && (
        <RichTextField
          label="A quem se destina"
          value={value.target_audience}
          onChange={(target_audience) => onChange({ target_audience })}
          placeholder="Escreva a quem se destina o evento aqui..."
          helperText={TARGET_AUDIENCE_HINT}
          minH="200px"
        />
      )}

      {has('topics') && (
        <RichTextField
          label="Temas abordados"
          value={value.topics}
          onChange={(topics) => onChange({ topics })}
          placeholder="Escreva os temas abordados no evento aqui..."
          helperText={TOPICS_HINT}
          minH="200px"
        />
      )}

      {has('term') && (
        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="flex" alignItems="center" gap={3}>
            <FormField label="Incluir termo" mb={0}>
              <Switch
                isChecked={value.use_term}
                onChange={(e) =>
                  onChange({ use_term: e.target.checked, term: e.target.checked ? (value.term ?? '') : null })
                }
                isDisabled={disabled}
              />
            </FormField>
          </Box>
          {value.use_term && (
            <RichTextField
              label="Conteúdo do termo"
              error={err('term')}
              value={value.term ?? ''}
              onChange={(term) => onChange({ term })}
              placeholder="Escreva o termo aqui..."
              minH="300px"
            />
          )}
        </Box>
      )}

      {has('testimonial') && (
        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="flex" alignItems="center" gap={3}>
            <FormField label="Incluir vídeo de depoimento" mb={0}>
              <Switch
                isChecked={value.use_testimonial}
                onChange={(e) =>
                  onChange({
                    use_testimonial: e.target.checked,
                    testimonial_title: e.target.checked ? (value.testimonial_title ?? '') : null,
                    testimonials: e.target.checked
                      ? value.testimonials.length > 0
                        ? value.testimonials
                        : [{ _id: crypto.randomUUID(), file: null, existingUrl: null }]
                      : []
                  })
                }
                isDisabled={disabled}
              />
            </FormField>
          </Box>
          {value.use_testimonial && (
            <Flex direction="column" gap={3}>
              <FormField label="Título do depoimento" error={err('testimonial_title')}>
                <Input
                  value={value.testimonial_title ?? ''}
                  onChange={(e) => onChange({ testimonial_title: e.target.value })}
                  placeholder="Ex: O que nossos alunos dizem"
                  disabled={disabled}
                />
              </FormField>
              <FormField label="Vídeos de depoimento (MP4)" error={err('testimonial_videos')}>
                <FormHelperText mt={0} mb={2}>
                  Adicione até 3 vídeos MP4 (até 100MB cada). Com mais de um, eles aparecem na vertical, lado a lado, no
                  site.
                </FormHelperText>
                <Flex direction="column" gap={4}>
                  {value.testimonials.map((slot, index) => (
                    <Box key={slot._id} borderWidth="1px" borderColor="gray.200" borderRadius="md" p={3}>
                      <Flex justify="space-between" align="center" mb={2}>
                        <Text variant="caption">Vídeo {index + 1}</Text>
                        {value.testimonials.length > 1 && (
                          <Button
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => onChange({ testimonials: value.testimonials.filter((_, i) => i !== index) })}
                            disabled={disabled}
                          >
                            Remover
                          </Button>
                        )}
                      </Flex>
                      <VideoFileInput
                        file={slot.file}
                        existingUrl={slot.existingUrl}
                        onChange={(file) =>
                          onChange({
                            testimonials: value.testimonials.map((s, i) => (i === index ? { ...s, file } : s))
                          })
                        }
                        onRemoveExisting={() =>
                          onChange({
                            testimonials: value.testimonials.map((s, i) =>
                              i === index ? { ...s, file: null, existingUrl: null } : s
                            )
                          })
                        }
                        isDisabled={disabled}
                        maxSizeMB={100}
                        error={errors[`${errorPrefix}testimonial_videos.${index}`]}
                      />
                    </Box>
                  ))}
                </Flex>
                {value.testimonials.length < 3 && (
                  <Button
                    mt={3}
                    size="sm"
                    variant="outline"
                    colorScheme="gray"
                    onClick={() =>
                      onChange({
                        testimonials: [
                          ...value.testimonials,
                          { _id: crypto.randomUUID(), file: null, existingUrl: null }
                        ]
                      })
                    }
                    disabled={disabled}
                  >
                    + Adicionar vídeo
                  </Button>
                )}
              </FormField>
            </Flex>
          )}
        </Box>
      )}

      {has('active_campaign') && (
        <Flex direction="column" gap={3}>
          <Text variant="caption">Listas do Active Campaign em que os contatos serão inscritos. Opcional.</Text>
          <FormControl isInvalid={!!err('active_campaign_lead_lists')}>
            <FormLabel display="flex" alignItems="center" gap={1.5}>
              Listas AC Leads
              <Tooltip label="Se nenhuma lista for selecionada, o comprador não é sincronizado no Active Campaign quando clica em 'Comprar ingressos' na página do evento — a tag iniciou-compra-evento não é aplicada.">
                <Icon name="lni lni-information" size="sm" color="gray.400" cursor="help" />
              </Tooltip>
            </FormLabel>
            <SearchableSelect
              isMulti
              options={activeCampaignLists.map((list) => ({ value: list.id, label: list.name }))}
              value={value.active_campaign_lead_lists}
              onChange={(lists) => onChange({ active_campaign_lead_lists: lists })}
              placeholder="Selecione uma ou mais listas"
              entity="lista"
              feminine
              isDisabled={disabled}
            />
            <FormHelperText>
              Apenas o comprador entra nessas listas, ao clicar em &quot;Comprar ingressos&quot; (antes do checkout).
            </FormHelperText>
            {err('active_campaign_lead_lists') && (
              <FormErrorMessage>{err('active_campaign_lead_lists')}</FormErrorMessage>
            )}
          </FormControl>
          <FormControl isInvalid={!!err('active_campaign_buyer_lists')}>
            <FormLabel display="flex" alignItems="center" gap={1.5}>
              Listas AC Compradores
              <Tooltip label="Se nenhuma lista for selecionada, o comprador não é sincronizado no Active Campaign quando o pagamento é confirmado (ou na inscrição, se o evento for gratuito) — a tag comprou-evento não é aplicada.">
                <Icon name="lni lni-information" size="sm" color="gray.400" cursor="help" />
              </Tooltip>
            </FormLabel>
            <SearchableSelect
              isMulti
              options={activeCampaignLists.map((list) => ({ value: list.id, label: list.name }))}
              value={value.active_campaign_buyer_lists}
              onChange={(lists) => onChange({ active_campaign_buyer_lists: lists })}
              placeholder="Selecione uma ou mais listas"
              entity="lista"
              feminine
              isDisabled={disabled}
            />
            <FormHelperText>Apenas o comprador entra nessas listas quando o pagamento é efetivado.</FormHelperText>
            {err('active_campaign_buyer_lists') && (
              <FormErrorMessage>{err('active_campaign_buyer_lists')}</FormErrorMessage>
            )}
          </FormControl>
        </Flex>
      )}

      {has('help_form') && (
        <FormControl isInvalid={!!err('use_help_form')} display="flex" flexDirection="column" gap={2}>
          <Box display="flex" alignItems="center" gap={3}>
            <Box display="flex" alignItems="center" gap={2}>
              <FormLabel mb={0} me={0}>
                Exibir formulário &quot;Tire suas dúvidas&quot;
              </FormLabel>
              {/* Fora do <label> e focável: dentro dele o clique alternaria o switch, e um
                  ícone solto não recebe foco — o Tooltip só abre no hover ou no foco. */}
              <Tooltip label={HELP_FORM_RULE_TEXT}>
                <Box as="span" tabIndex={0} role="img" aria-label="Ajuda" display="inline-flex" cursor="help">
                  <Icon name="lni lni-information" size="sm" color="gray.400" />
                </Box>
              </Tooltip>
            </Box>
            <Switch
              isChecked={helpFormAllowed && value.use_help_form}
              onChange={(e) => onChange({ use_help_form: e.target.checked })}
              isDisabled={disabled || !helpFormAllowed}
            />
          </Box>
          <Text variant="caption">
            Quando ativado, um botão flutuante de WhatsApp com o formulário &quot;Tire suas dúvidas&quot; aparece na
            página pública do evento.
          </Text>
          {!helpFormAllowed && (
            <Text fontSize="sm" color="orange.600">
              {helpFormBlockedHint ?? HELP_FORM_BLOCKED_HINT}
            </Text>
          )}
          <FormErrorMessage>{err('use_help_form')}</FormErrorMessage>
        </FormControl>
      )}

      {has('location') && needsLocation(formatForLocation) && (
        <Box>
          <FormField label="Local do Evento">
            <LocationFields
              value={value}
              onChange={(patch) => onChange(patch)}
              disabled={disabled}
              errors={errors}
              errorPrefix={errorPrefix}
            />
          </FormField>
        </Box>
      )}
    </Flex>
  )
}
